import * as React from "react";
import { useToasts } from "react-toast-notifications";
import { Button } from "../../../bits/Button/Button";
import Alert from "../../../bits/Alert/Alert";
import { AlertDialog } from "../../../bits/AlertDialog/AlertDialog";
import { MetaBox } from "../../../bits/MetaBox/MetaBox";
import Skeleton from "../../../bits/Skeleton/Skeleton";
import Table from "../../../bits/Table/Table";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import RefreshIcon from "../../../icons/Refresh";
import { displayPhoneNumber } from "../../../../../utils/utils";
import DirectionIcon from "../DirectionIcon/DirectionIcon";
import formatCommunicationHistoryTimestamp from "../formatCommunicationHistoryTimestamp";
import { useLeaWorkflow } from "./LeaWorkflowContext";
import {
    LeaHistoryEntry,
    leaGuidance,
    leaStatusSummary,
    leaUnverifiedSessionEnded,
} from "./leaWorkflow";
import styles from "./LeaConversation.module.scss";

const SENDER_LABELS: Record<LeaHistoryEntry["role"], string> = {
    lead: "Lead",
    assistant: "Lea AI",
    staff: "Intake",
};

type ConfirmAction = "takeover" | "end_test";

export default function LeaConversation() {
    const lea = useLeaWorkflow();
    const { addToast } = useToasts();
    const [confirmAction, setConfirmAction] = React.useState<ConfirmAction | null>(
        null
    );

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                id: "sent-on",
                accessor: (row: LeaHistoryEntry) => (
                    <p>
                        <DirectionIcon
                            direction={row.role === "lead" ? "Inbound" : "Outbound"}
                        />
                        {formatCommunicationHistoryTimestamp(row.at) || "--"}
                    </p>
                ),
            },
            {
                Header: "From",
                accessor: (row: LeaHistoryEntry) => <p>{SENDER_LABELS[row.role]}</p>,
            },
            {
                Header: "Content",
                accessor: (row: LeaHistoryEntry) => (
                    <p className={styles.messageContent}>{row.text}</p>
                ),
            },
        ],
        []
    );

    if (!lea) return null;
    const { workflow, loading, pending, error } = lea;

    const refreshButton = (
        <Button
            variant="normal"
            className={styles.refreshButton}
            disabled={pending}
            onClick={() => lea.refresh()}
            aria-label="Refresh Lea AI conversation"
            title="Refresh conversation"
        >
            <RefreshIcon />
        </Button>
    );

    if (loading && !workflow) {
        return <Skeleton count={3} />;
    }
    if (!workflow) {
        return error ? (
            <div className={styles.toolbar}>
                <Alert type="warning" className={styles.fill}>
                    Lea AI conversation could not be loaded. Please try again.
                </Alert>
                {refreshButton}
            </div>
        ) : null;
    }

    const summary = leaStatusSummary(workflow);
    const guidance = leaGuidance(workflow);
    const uncertain = ["sending", "send_unknown"].includes(workflow.status);
    const canEndTest =
        (workflow.outbound_enabled || uncertain) &&
        !leaUnverifiedSessionEnded(workflow);

    const confirmAndRun = async () => {
        const action = confirmAction;
        setConfirmAction(null);
        if (!action) return;
        const succeeded = await lea.runAction(action);
        addToast(
            succeeded
                ? action === "takeover"
                    ? "Intake has taken over from Lea AI."
                    : "The Lea AI test session has ended."
                : "Could not update Lea AI. Please try again.",
            { appearance: succeeded ? "success" : "error" }
        );
    };

    return (
        <div>
            <div className={styles.toolbar}>
                <div className={styles.details}>
                    <MetaBox
                        label="Status"
                        value={
                            <span className={styles[summary.tone]}>
                                {summary.label}
                            </span>
                        }
                    />
                    {workflow.test_recipient && (
                        <MetaBox
                            label="Test number"
                            value={displayPhoneNumber(workflow.test_recipient)}
                        />
                    )}
                </div>
                <div className={styles.actions}>
                    {refreshButton}
                    {summary.canTakeOver && (
                        <Button
                            disabled={pending}
                            onClick={() => setConfirmAction("takeover")}
                        >
                            Take over from Lea AI
                        </Button>
                    )}
                    {canEndTest && (
                        <Button
                            variant="secondary"
                            disabled={pending}
                            onClick={() => setConfirmAction("end_test")}
                        >
                            End test session
                        </Button>
                    )}
                </div>
            </div>
            {guidance && (
                <Alert type={guidance.type} className={styles.guidance}>
                    {guidance.text}
                </Alert>
            )}
            {error && (
                <Alert type="warning" className={styles.guidance}>
                    Lea AI could not be updated. Please try again.
                </Alert>
            )}
            {workflow.history.length === 0 ? (
                <EmptyListState
                    emptyMessage="No Messages Yet"
                    stateHelperText="Lea AI has not sent or received any messages for this client yet."
                />
            ) : (
                <Table
                    columns={columns}
                    data={[...workflow.history].reverse()}
                    allowSelect={false}
                />
            )}
            <AlertDialog
                open={confirmAction !== null}
                onClose={() => setConfirmAction(null)}
                title={
                    confirmAction === "takeover"
                        ? "Take over from Lea AI?"
                        : "End Lea AI test session?"
                }
                description={
                    confirmAction === "takeover"
                        ? "Lea AI will stop texting this client and Intake will handle the conversation."
                        : uncertain
                        ? "Check the RingCentral message log first. Ending this test while the last SMS is unverified permanently locks this inquiry and test number."
                        : "Lea AI will stop texting this test number and the session will end."
                }
                saveText={
                    confirmAction === "takeover" ? "Take over" : "End test session"
                }
                onSaveClick={confirmAndRun}
                isDeleting={confirmAction === "end_test"}
            />
        </div>
    );
}
